import { jwtDecode } from '@quadminds/shared/utils';
import { isStorkPlatform } from './utils';
import { getAuthService } from '../services/auth.service';

type HeaderValue = string;

interface BuildAgentsHeadersOptions {
    accept?: HeaderValue;
    contentType?: HeaderValue;
    extraHeaders?: Record<string, HeaderValue>;
}

type MintedTokens = { accessToken?: string; refreshToken?: string } | undefined;

/**
 * Copia autoritativa del access token, en memoria del módulo.
 *
 * `localStorage` está compartido por todas las apps servidas desde
 * `<host>/apps/*` (mismo origen) y todas usan la clave 'token'. El
 * `authInterceptor` de route-tracking (`routeTracking.service.ts`) llama
 * `clearTokensFromStorage()` y guarda el token que mintea sólo en memoria
 * propia: borra el de Mindy sin reponerlo. El siguiente request a agents-api
 * sale sin `Authorization` y el backend responde
 * 401 {"code":"UNAUTHORIZED","message":"Missing authentication token"}.
 *
 * Manteniendo la copia acá, nadie fuera de este módulo puede dejarnos sin token.
 */
let inMemoryToken: string | null = null;

/**
 * Margen antes del `exp` real para considerar un token inservible.
 * Los tokens de auth-service viven 900s (15 min). Sin margen, un token con dos
 * segundos de vida pasa el chequeo y muere en vuelo — el stream es un request
 * largo, así que conviene renovar antes de llegar al filo.
 */
const TOKEN_EXPIRY_SKEW_MS = 30_000;

/**
 * `localStorage` puede lanzar cuando el navegador bloquea el storage de
 * terceros. Mindy corre embebido en un iframe cross-site, así que todo acceso
 * va protegido y la memoria queda como único store si falla.
 */
const safeGetItem = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const safeSetItem = (key: string, value: string): void => {
    try {
        localStorage.setItem(key, value);
    } catch {
        // La copia en memoria alcanza para operar.
    }
};

const isTokenUsable = (token: string | null): boolean => {
    if (!token) return false;
    try {
        const { payload } = jwtDecode(token);
        const exp = payload?.exp;
        if (typeof exp !== 'number') return false;
        return exp * 1000 - TOKEN_EXPIRY_SKEW_MS > Date.now();
    } catch {
        // Token ilegible: tratarlo como inservible y dejar que se renueve.
        return false;
    }
};

/**
 * Registra un token recién emitido. La llaman los puntos que hoy escriben
 * `localStorage.token` (bootstrap de auth, interceptor de chat.service) para
 * que la copia en memoria quede poblada desde el arranque.
 */
export const rememberAccessToken = (token?: string | null): void => {
    if (!token) return;
    inMemoryToken = token;
    safeSetItem('token', token);
};

/** Memoria primero; `localStorage` sólo como semilla de arranque en frío. */
const readUsableToken = (): string | null => {
    if (isTokenUsable(inMemoryToken)) return inMemoryToken;

    const stored = safeGetItem('token');
    if (isTokenUsable(stored)) {
        inMemoryToken = stored;
        return stored;
    }

    return null;
};

/**
 * Pide un token nuevo a auth-service y lo persiste.
 * Mismo orden que `useAuthSystem.initializeAuth`: primero `refreshToken()`
 * (cookie de refresh), y si falla `checkSession()` (cookie de sesión de
 * plataforma). Devuelve null si ninguna de las dos pudo emitir uno usable.
 */
const mintAccessToken = async (): Promise<string | null> => {
    const authService = getAuthService();
    const strategies: Array<() => Promise<MintedTokens>> = [
        () => authService.refreshToken(),
        () => authService.checkSession(),
    ];

    for (const mint of strategies) {
        try {
            const result = await mint();
            if (result?.accessToken && isTokenUsable(result.accessToken)) {
                rememberAccessToken(result.accessToken);
                if (result.refreshToken) safeSetItem('refreshToken', result.refreshToken);
                return result.accessToken;
            }
        } catch {
            // Probar la siguiente estrategia.
        }
    }

    return null;
};

/** Devuelve un accessToken vigente, renovándolo si no hay ninguno usable. */
export const ensureFreshAccessToken = async (): Promise<string | null> => {
    const current = readUsableToken();
    if (current) return current;
    return (await mintAccessToken()) ?? inMemoryToken;
};

/**
 * Fuerza la emisión de un token nuevo, ignorando el `exp` del actual.
 * Para el caso en que el token *parece* vigente pero el backend lo rechaza
 * (sesión revocada por un logout de otra app del mismo origen).
 */
export const forceRefreshAccessToken = (): Promise<string | null> => mintAccessToken();

export const buildAgentsHeaders = ({ accept, contentType, extraHeaders = {} }: BuildAgentsHeadersOptions = {}): Record<
    string,
    HeaderValue
> => {
    const token = readUsableToken();

    return {
        ...extraHeaders,
        ...(accept !== undefined ? { Accept: accept } : {}),
        ...(contentType !== undefined ? { 'Content-Type': contentType } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(isStorkPlatform() ? { 'X-Auth-Provider': 'stork' } : {}),
    };
};

/**
 * Igual que `buildAgentsHeaders` pero garantizando que haya un token vigente.
 * Usarla en todo llamado a agents-api que no pase por `agentsApi.httpClient`
 * (que ya tiene refresh-on-401 en `chat.service.setupAuthInterceptor`).
 */
export const buildFreshAgentsHeaders = async (
    options: BuildAgentsHeadersOptions = {},
): Promise<Record<string, HeaderValue>> => {
    await ensureFreshAccessToken();
    return buildAgentsHeaders(options);
};

export const isCancelledRequestError = (error: unknown): boolean => {
    const candidate = error as { code?: string; name?: string };
    return (
        candidate?.code === 'ERR_CANCELED' || candidate?.name === 'AbortError' || candidate?.name === 'CanceledError'
    );
};
